import { useEffect, useState, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import { Modal, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import useSessionState from "../../hooks/useSessionState";
import PageContentShell from "../../components/layout/PageContentShell";
import CustomerListPanel from "./components/CustomerListPanel";
import apiClient from "../../helpers/apiClient";
import { useToast } from "../../contexts/ToastContext";
import { DataTable, useTableFilters, RowActionsMenu } from "../../components/data-table";
import deleteFile from "../../utilities/deleteFile";

function Customers() {
    const { t } = useTranslation("customers");
    const history = useHistory();
    const toast = useToast();

    const [customers, setCustomers] = useState([]);
    const [pageSize, setPageSize] = useSessionState("customers:pageSize", 10);
    const [currentPage, setCurrentPage] = useSessionState("customers:page", 1);
    const [totalItemCount, setTotalItemCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const table = useTableFilters({ persistKey: "customers", onChange: () => setCurrentPage(1) });
    const searchParams = table.filters;

    const fetchCustomers = useCallback(async (page = 1, pageSizeParam = 10, searchParameters = {}) => {
        const queryParams = new URLSearchParams({
            limit: pageSizeParam.toString(),
            page: page.toString(),
            ...searchParameters,
        });

        try {
            setLoading(true);
            const response = await apiClient.get(`/api/user/list?${queryParams}`);
            if (response?.data?.success) {
                const { result, meta } = response.data.data;
                const totalItems = meta?.total || 0;

                setTotalItemCount(totalItems);
                setCustomers(result);
            }
        } catch (_error) {
            // Handle error silently or show a user-friendly message
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCustomers(currentPage, pageSize, searchParams);
    }, [currentPage, pageSize, searchParams, fetchCustomers]);

    const handleDeleteCustomer = (user) => {
        Modal.confirm({
            title: t("confirmDelete"),
            okText: t("delete"),
            cancelText: t("cancel"),
            centered: true,
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const res = await apiClient.delete(`/api/user/${user.id}`);

                    if (user.userImage) {
                        await deleteFile(user.userImage);
                    }

                    if (res?.data?.success) {
                        await fetchCustomers(currentPage, pageSize, searchParams);
                        toast.success({ message: t("deleteSuccess") });
                        return;
                    }

                    toast.error({ message: t("deleteError") });
                    throw new Error("Delete failed");
                } catch (_error) {
                    toast.error({ message: t("deleteError") });
                    throw _error;
                }
            },
        });
    };

    const handleActionClick = async (key, user) => {
        if (key === "1") {
            history.push(`/customers/${user.id}`);
        } else if (key === "2") {
            handleDeleteCustomer(user);
        }
    };

    const customerActionItems = [
        { label: t("viewCustomer"), key: "1" },
        { label: t("deleteCustomer"), key: "2", danger: true },
    ];

    const renderActionMenu = (user) => (
        <RowActionsMenu
            items={customerActionItems}
            onClick={({ key }) => handleActionClick(key, user)}
            label={t("action")}
        />
    );

    const columns = [
        {
            title: t("name"),
            key: "name",
            ellipsis: true,
            width: 220,
            ...table.getColumnSearchProps("searchTerm", { placeholder: t("listSearchPlaceholder") }),
            render: (_, user) => {
                const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");
                return (
                    <Link to={`/customers/${user.id}`} className="sc-table-link">
                        {fullName || t("common.notAvailable")}
                    </Link>
                );
            },
        },
        {
            title: t("email"),
            dataIndex: "email",
            ellipsis: true,
            width: 220,
            ...table.getColumnSearchProps("email"),
            render: (email) => <span className="text-sm text-gray-700">{email || t("common.notAvailable")}</span>,
        },
        {
            title: t("phone"),
            dataIndex: "mobileNumber",
            width: 150,
            ...table.getColumnSearchProps("mobileNumber"),
            render: (mobileNumber) => (
                <span className="text-sm tabular-nums">{mobileNumber || t("common.notAvailable")}</span>
            ),
        },
        {
            title: t("licenseNumber"),
            dataIndex: "drivingLicense",
            width: 250,
            ...table.getColumnSearchProps("drivingLicense.number"),
            render: (drivingLicense) => {
                if (!drivingLicense || !drivingLicense.number) {
                    return <span className="text-sm text-gray-400">Not Verified</span>;
                }

                const getStatusColor = (status) => {
                    switch (status?.toUpperCase()) {
                        case "PASSED":
                            return "success";
                        case "FAILED":
                            return "error";
                        case "PENDING":
                            return "warning";
                        default:
                            return "default";
                    }
                };

                const formatDate = (date) => {
                    if (!date) return "N/A";
                    return new Date(date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    });
                };

                return (
                    <Tooltip
                        title={
                            <div className="space-y-1">
                                <div>
                                    <strong>Name:</strong> {drivingLicense.firstName} {drivingLicense.lastName}
                                </div>
                                <div>
                                    <strong>License #:</strong> {drivingLicense.number}
                                </div>
                                <div>
                                    <strong>Birth Date:</strong> {formatDate(drivingLicense.birthDate)}
                                </div>
                                <div>
                                    <strong>Expire Date:</strong> {formatDate(drivingLicense.expireDate)}
                                </div>
                                <div>
                                    <strong>State:</strong> {drivingLicense.state || "N/A"}
                                </div>
                                <div>
                                    <strong>Country:</strong> {drivingLicense.country || "N/A"}
                                </div>
                                {drivingLicense.status && (
                                    <div>
                                        <strong>Status:</strong>{" "}
                                        <Tag color={getStatusColor(drivingLicense.status)} className="ml-1">
                                            {drivingLicense.status.toUpperCase()}
                                        </Tag>
                                    </div>
                                )}
                                {drivingLicense.driverApplicantId && (
                                    <div className="text-xs text-gray-300 mt-2">
                                        ID: {drivingLicense.driverApplicantId}
                                    </div>
                                )}
                            </div>
                        }>
                        <span className="inline-flex cursor-help items-center gap-2">
                            <span className="sc-mono">{drivingLicense.number}</span>
                            {drivingLicense.status && (
                                <Tag color={getStatusColor(drivingLicense.status)} className="!m-0 text-[11px]">
                                    {drivingLicense.status.toUpperCase()}
                                </Tag>
                            )}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: <span className="sr-only">{t("action")}</span>,
            key: "action",
            width: 64,
            align: "center",
            fixed: "right",
            render: (_, user) => renderActionMenu(user),
        },
    ];

    const desktopCustomers = (
        <div className="hidden lg:block" data-tour="customers-table">
            <DataTable
                title={t("customerList")}
                count={totalItemCount}
                filters={table}
                search={{ key: "searchTerm", placeholder: t("listSearchPlaceholder") }}
                filterLabels={{
                    email: t("email"),
                    mobileNumber: t("phone"),
                    "drivingLicense.number": t("licenseNumber"),
                }}
                columns={columns}
                dataSource={customers}
                loading={loading}
                rowKey="id"
                scroll={{ x: 1000 }}
                pagination={{
                    total: totalItemCount,
                    current: currentPage,
                    pageSize,
                    onChange: setCurrentPage,
                    onPageSizeChange: setPageSize,
                }}
            />
        </div>
    );

    return (
        <PageContentShell>
            <div className="flex min-w-0 flex-col gap-4 lg:gap-0">
                <div className="flex min-w-0 flex-col gap-4 lg:hidden">
                    <CustomerListPanel
                        t={t}
                        customers={customers}
                        loading={loading}
                        totalItemCount={totalItemCount}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        filters={table}
                        customerActionItems={customerActionItems}
                        onActionClick={handleActionClick}
                    />
                </div>
                {desktopCustomers}
            </div>
        </PageContentShell>
    );
}

export default Customers;
